import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Info } from "lucide-react";
import axios from "axios";
import { toast } from 'react-toastify';
import "../../assets/css/KnowledgeBase.css";
import "../../assets/css/Urls.css";
import { getProjectPid } from "../../utils/sessionProject";

const BASE = process.env.REACT_APP_BASE_URL;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const ALLOWED_MIME_TYPES = [
  "text/plain",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];
const ALLOWED_EXTENSIONS = [".txt", ".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png", ".gif", ".webp"];

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (file) => {
  if (!file) return "fa-file";
  if (file.type === "application/pdf") return "fa-file-pdf";
  if (file.type === "text/plain") return "fa-file-lines";
  if (file.type.startsWith("image/")) return "fa-file-image";
  if (
    file.type === "application/msword" ||
    file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return "fa-file-word";
  }
  return "fa-file";
};

const isAllowedFile = (file) => {
  if (!file) return false;
  if (ALLOWED_MIME_TYPES.includes(file.type)) return true;
  // Some browsers/OSes don't set a MIME type for .doc/.docx — fall back to extension check.
  return ALLOWED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));
};

const KnowledgeBaseCreate = () => {
  const navigate = useNavigate();
  const projectPid = getProjectPid();
  const fileInputRef = useRef(null);

  const [mode, setMode] = useState("write"); // "write" | "upload"

  const [title,      setTitle]      = useState("");
  const [rawContent, setRawContent] = useState("");
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState("");

  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadFile,  setUploadFile]  = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState(null);

  useEffect(() => {
    if (!uploadFile || !uploadFile.type.startsWith("image/")) {
      setUploadPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(uploadFile);
    setUploadPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [uploadFile]);

  const handleSave = async () => {
    if (!title.trim())      { setError("Title is required."); return; }
    if (!rawContent.trim()) { setError("Content is required."); return; }
    setError("");
    setSaving(true);
    try {
      const res = await axios.post(
        `${BASE}/api/v1/knowledge-base/write`,
        { projectPid, title: title.trim(), rawContent: rawContent.trim() },
        { headers: getAuthHeader() }
      );
      if (res.data?.status) {
        toast.success("Document saved successfully!");
        navigate("/knowledge-base");
      } else {
        setError(res.data?.message || "Failed to save document.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Network error. Please try again.");
    }
    setSaving(false);
  };

  const pickUploadFile = (file) => {
    if (!file) return;
    if (!isAllowedFile(file)) {
      setError("Only TXT, PDF, DOCX, JPEG, PNG, GIF, and WEBP files are allowed.");
      return;
    }
    setError("");
    setUploadFile(file);
    if (!uploadTitle.trim()) {
      setUploadTitle(file.name.replace(/\.[^/.]+$/, ""));
    }
  };

  const handleUploadSave = async () => {
    if (!uploadFile) { setError("Please select a file to upload."); return; }
    setError("");
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("projectPid", projectPid);
      if (uploadTitle.trim()) formData.append("title", uploadTitle.trim());
      formData.append("file", uploadFile);

      const res = await axios.post(
        `${BASE}/api/v1/knowledge-base/upload`,
        formData,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } }
      );

      if (res.data?.status) {
        const data = res.data?.response;
        if (data?.extractionMethod === "ocr") {
          toast.warn("This was read via OCR — please review the extracted title/content before marking it ready.");
        } else {
          toast.success("Document uploaded and processed successfully!");
        }
        navigate("/knowledge-base");
      } else {
        setError(res.data?.message || "Failed to upload document.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Network error. Please try again.");
    }
    setUploading(false);
  };

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">
        <div className="kb-create-card">

          {/* Header */}
          <div className="kb-create-header">
            <div>
              <h5 className="kb-create-title">Create Document</h5>
              <p className="kb-create-sub">Add a new knowledge base document</p>
            </div>
            <div className="kb-create-actions">
              <button
                className="btn btn-outline-secondary kb-create-cancel"
                onClick={() => navigate("/knowledge-base")}
                disabled={saving || uploading}
              >
                Cancel
              </button>
              {mode === "write" ? (
                <button
                  className="btn btn-primary kb-create-save"
                  onClick={handleSave}
                  disabled={saving || !title.trim() || !rawContent.trim()}
                >
                  {saving ? "Saving..." : "Save Document"}
                </button>
              ) : (
                <button
                  className="btn btn-primary kb-create-save"
                  onClick={handleUploadSave}
                  disabled={uploading || !uploadFile}
                >
                  {uploading ? "Uploading..." : "Upload Document"}
                </button>
              )}
            </div>
          </div>

          {/* Mode tabs */}
          <div className="kb-create-mode-tabs">
            <button
              type="button"
              className={`kb-create-mode-tab ${mode === "write" ? "active" : ""}`}
              onClick={() => { setMode("write"); setError(""); }}
            >
              <i className="fa-solid fa-pen-to-square"></i>
              Write Content
            </button>
            <button
              type="button"
              className={`kb-create-mode-tab ${mode === "upload" ? "active" : ""}`}
              onClick={() => { setMode("upload"); setError(""); }}
            >
              <i className="fa-solid fa-file-arrow-up"></i>
              Upload File
            </button>
          </div>

          {error && (
            <div className="alert alert-danger py-2 px-3 mb-0" style={{ fontSize: 13, borderRadius: 8 }}>
              {error}
            </div>
          )}

          {mode === "write" ? (
            <>
              <p className="kb-create-note">
                <Info size={14} className="kb-create-note-icon" />
                <span>
                  New documents are saved as <strong>draft</strong>. Mark it as ready from the
                  document detail page to queue it for chunking — chunks appear after the next
                  scheduled chunking run, not immediately.
                </span>
              </p>

              {/* Title */}
              <div className="kb-create-field">
                <label className="kb-create-label">Title <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control kb-create-input"
                  placeholder="Enter document title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              {/* Raw Content */}
              <div className="kb-create-field">
                <label className="kb-create-label">Content <span className="text-danger">*</span></label>
                <textarea
                  className="form-control kb-create-textarea"
                  placeholder="Paste or type your raw document content here."
                  value={rawContent}
                  onChange={(e) => setRawContent(e.target.value)}
                  rows={18}
                />
              </div>
            </>
          ) : (
            <>
              <p className="kb-create-note">
                <Info size={14} className="kb-create-note-icon" />
                <span>
                  Upload a TXT, PDF, DOCX, or image (JPEG/PNG/GIF/WEBP) file to create a document
                  from its extracted text. New documents are saved as <strong>draft</strong> — mark
                  it as ready from the document detail page to queue it for chunking. Scanned PDFs
                  and images are processed via OCR and may take longer.
                </span>
              </p>

              {/* Title */}
              <div className="kb-create-field">
                <label className="kb-create-label">Title <span className="text-muted" style={{ fontWeight: 400 }}>(optional — uses the extracted title if left blank)</span></label>
                <input
                  type="text"
                  className="form-control kb-create-input"
                  placeholder="Enter document title..."
                  maxLength={255}
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                />
              </div>

              {/* File upload */}
              <div className="kb-create-field">
                <label className="kb-create-label">File <span className="text-danger">*</span></label>

                {uploadFile ? (
                  <div className="kb-pdf-file-card">
                    {uploadPreviewUrl ? (
                      <img src={uploadPreviewUrl} alt={uploadFile.name} className="kb-pdf-file-thumb" />
                    ) : (
                      <div className="kb-pdf-file-icon">
                        <i className={`fa-solid ${getFileIcon(uploadFile)}`}></i>
                      </div>
                    )}
                    <div>
                      <p className="kb-pdf-file-name">{uploadFile.name}</p>
                      <p className="kb-pdf-file-size">{formatFileSize(uploadFile.size)}</p>
                    </div>
                    <button
                      type="button"
                      className="kb-pdf-file-remove"
                      onClick={() => {
                        setUploadFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      aria-label="Remove file"
                      disabled={uploading}
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                ) : (
                  <div
                    className={`kb-pdf-dropzone ${isDragging ? "dragging" : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      pickUploadFile(e.dataTransfer.files?.[0]);
                    }}
                  >
                    <div className="kb-pdf-dropzone-icon">
                      <i className="fa-solid fa-cloud-arrow-up"></i>
                    </div>
                    <p className="kb-pdf-dropzone-title">
                      Drag &amp; drop your file here, or{" "}
                      <span className="kb-pdf-browse-link">browse</span>
                    </p>
                    <p className="kb-pdf-dropzone-sub">Supports .txt, .pdf, .doc, .docx, .jpg, .png, .gif, .webp files</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".txt,.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,text/plain,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/gif,image/webp"
                      hidden
                      onChange={(e) => pickUploadFile(e.target.files?.[0])}
                    />
                  </div>
                )}
              </div>
            </>
          )}

        </div>
      </div>
    </main>
  );
};

export default KnowledgeBaseCreate;
